import React, { useEffect } from "react";
import styled from "styled-components";
import { SYLLABUS_IMPORTS } from "../../utils/constants/labels";
import PageHeader from "../common/layout/PageHeader";
import { aiApi } from "../../api/aiApi";
import colors from "../../utils/colors";
import { StyledAntTable } from "../common/table/Table.style";
import { SyllabusImport } from "../../types/aiTypes";
import { formatDate } from "../../utils/timeUtils";
import { DatePreview } from "../../utils/helpers";
import Button from "../common/buttons/Button";

const ITEMS_PER_PAGE = 20;

const statusColors = {
  completed: colors.positive,
  pending: colors.main2,
  'in_progress': colors.warning,
  failed: colors.negative,
};

const columns = () => [
  {
    title: 'UserID',
    dataIndex: 'user_id',
    width: '5%',
  },
  {
    title: 'Created At',
    dataIndex: 'created_at',
    width: '10%',
    render: value => (value ? <DatePreview>{formatDate(value, 'hh:mm a, MMM DD')}</DatePreview> : null)
  },
  {
    title: 'Status',
    dataIndex: 'status',
    width: '10%',
    render: value => <span style={{ color: statusColors[value] }}>{value}</span>
  },
  {
    title: 'Raw File',
    dataIndex: 'raw_file_name',
    width: '20%',
    render: (value, row) => (
      <LinkButton onClick={async () => {
        const res = await aiApi.getSyllabusFileDownloadUrl(value, row["user_id"], 'raw');
        if (res.ok && res.data) {
          window.open(res.data.url, "_blank", "noopener,noreferrer");
        }
      }}
      >
        {value}
      </LinkButton>
    )
  },
  {
    title: 'Parsed File',
    dataIndex: 'parsed_file_name',
    width: '20%',
    render: (value, row) => (
      <LinkButton onClick={async () => {
        const res = await aiApi.getSyllabusFileDownloadUrl(value, row["user_id"], 'parsed');
        if (res.ok && res.data) {
          window.open(res.data.url, "_blank", "noopener,noreferrer");
        }
      }}
      >
        {value}
      </LinkButton>
    )
  },
  {
    title: 'Tokens (i + o = t)',
    dataIndex: 'tokens',
    width: '20%',
    render: (value, row) => {
      if (!row['total_tokens']) {
        return null;
      }
      return <span>{row['input_tokens']} + {row['output_tokens']} = {row['total_tokens']}</span>;
    }
  },
  {
    title: 'Acknowledged',
    dataIndex: 'acknowledged',
    width: '15%',
    render: (value, row) => {
      const status = row['status'];
      if (!['completed', 'failed'].includes(status)) {
        return null;
      }
      if (status === 'completed') {
        if (row['imported'] === null || row['imported'] === undefined) {
          return 'No';
        }
        return row['imported'] ? 'Yes (Imported)' : 'Yes (Import Cancelled)';
      }
      if (status === 'failed') {
        return row['failure_acknowledged'] ? 'Yes' : 'No';
      }
    }
  },
];

const SyllabusImports = () => {
  const [data, setData] = React.useState<SyllabusImport[]>();
  const [cursor, setCursor] = React.useState<string>();
  const [loadingMore, setLoadingMore] = React.useState<boolean>(false);

  useEffect(() => {
    const init = async () => {
      const res = await aiApi.getSyllabusImports(ITEMS_PER_PAGE);
      if (res.ok) {
        setData(res.data?.items);
        setCursor(res.data?.cursor);
      }
    }

    init();
  }, []);

  const loadMore = async () => {
    setLoadingMore(true);
    const res = await aiApi.getSyllabusImports(ITEMS_PER_PAGE, cursor);
    if (res.ok) {
      setData(prev => [...(prev || []), ...(res.data?.items || [])]);
      setCursor(res.data?.cursor);
      setLoadingMore(false);
    }
  }

  return (
    <Container>
      <PageHeader
        title={SYLLABUS_IMPORTS}
      />
      <StyledAntTable
        columns={columns()}
        rowKey={record => `${record['user_id']}-${record['created_at']}`}
        dataSource={data ?? []}
        pagination={false}
      />
      <LoadMoreWrapper>
        {cursor && (
          <Button onClick={loadMore} loading={loadingMore}>
            Load More
          </Button>
        )}
      </LoadMoreWrapper>
    </Container>
  );
}

export default SyllabusImports;

const toSyllabusFileS3Url = (userId: number, fileName: string, fileType: 'raw' | 'parsed') => {
  return `${process.env.REACT_APP_S3_BASE_URL}/syllabi/${fileType}/${userId}/${fileName}`;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  flex: 1;
  background-color: ${colors.white};
  position: relative;
`;

const LoadMoreWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px 0;
`;

export const LinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  text-align: left;

  font: inherit;
  color: ${colors.primaryPurple};
  cursor: pointer;

  &:hover {
    opacity: 0.85;
  }
`;
